import React, { useMemo, useState } from 'react';
import Button from './Button';
import './ReprocessProfilesModal.css';

// Wizard shown when the user reprocesses records whose error names a missing LINKED RECORD — a source
// profile, or another reservation/block the record depends on. It lists what is missing per record and
// lets the user choose which to also request. Checked items are re-synced FIRST, then the records.
//
// The selection is keyed and returned as "<Kind>|<id>" (Profile | Reservation | Block). The kind matters:
// the backend maps it to the source module it requests (REQUEST_MODULE in ui-v2 sync update.js), so a
// missing Reservation is fetched as a RESERVATION. Returning a bare id made everything a PROFILE request,
// which for a reservation id fetches nothing and creates a dead record.
//
// Props:
//   items:        [{ record: {sourcerecordid, scopeType, recordid}, missingProfiles: [{profileId, profileType, kind}] }]
//   otherCount:   number of other selected records being reprocessed that have nothing missing
//   processing:   disables the actions while the request is in flight
//   onCancel():   close without reprocessing
//   onConfirm(selectedKeys: string[]): reprocess, requesting the checked records first ("<Kind>|<id>")
const ReprocessProfilesModal = ({ items = [], otherCount = 0, processing = false, onCancel, onConfirm }) => {
    // Distinct missing records across the selection, checked by default. Keyed by KIND+id — a
    // Profile 123 and a Reservation 123 are different records and must not collapse into one checkbox.
    const keyOf = (p) => `${p.kind || 'Profile'}|${p.profileId}`;
    const allProfileIds = useMemo(() => {
        const ids = [];
        const seen = new Set();
        items.forEach(it => it.missingProfiles.forEach(p => {
            const k = keyOf(p);
            if (!seen.has(k)) { seen.add(k); ids.push(k); }
        }));
        return ids;
    }, [items]);

    const [checked, setChecked] = useState(() => new Set(allProfileIds));

    const toggle = (key) => {
        setChecked(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const allChecked = allProfileIds.length > 0 && allProfileIds.every(id => checked.has(id));
    const toggleAll = () => setChecked(allChecked ? new Set() : new Set(allProfileIds));

    const recordCount = items.length + otherCount;
    const selectedCount = checked.size;

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-content rpm-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Reprocess &amp; request missing linked records</h2>
                    <button className="modal-close" onClick={onCancel}>×</button>
                </div>
                <div className="modal-body">
                    <p>
                        {items.length} of the {recordCount} selected record(s) reference a linked record
                        that has not been synced to the target. Choose which to request — they are
                        re-synced <strong>first</strong>, then the records are reprocessed.
                    </p>

                    {allProfileIds.length > 1 && (
                        <label className="rpm-selectall">
                            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                            Select all ({allProfileIds.length})
                        </label>
                    )}

                    <div className="rpm-list">
                        {items.map(({ record, missingProfiles }) => (
                            <div key={record.recordid} className="rpm-row">
                                <div className="rpm-row-head">
                                    <span className="rpm-scope">{record.scopeType || 'RECORD'}</span>
                                    <span className="rpm-recid">{record.sourcerecordid || record.recordid}</span>
                                </div>
                                <div className="rpm-profiles">
                                    {missingProfiles.map(p => (
                                        <label key={keyOf(p)} className="rpm-profile">
                                            <input
                                                type="checkbox"
                                                checked={checked.has(keyOf(p))}
                                                onChange={() => toggle(keyOf(p))}
                                            />
                                            {/* The kind is shown, not just the id — "26713303" alone does not
                                                say whether the pipeline will fetch a profile or a reservation. */}
                                            <span className="rpm-kind">{p.kind || 'Profile'}</span>
                                            <span className="rpm-pid">{p.profileId}</span>
                                            {p.profileType && <span className="rpm-ptype">{p.profileType}</span>}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="modal-footer">
                    <Button variant="secondary" onClick={onCancel} disabled={processing}>Cancel</Button>
                    <Button onClick={() => onConfirm(Array.from(checked))} disabled={processing}>
                        {selectedCount > 0
                            ? `Request ${selectedCount} linked record(s) & reprocess`
                            : `Reprocess ${recordCount} record(s)`}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ReprocessProfilesModal;
